import { NextPage } from 'next';
import { Store } from 'redux';
import type { LogicMiddleware } from 'redux-logic';

import {
  categoryTreeSelector,
  getDetail as getCategoryDetail,
  getCategoryTree,
  jobCategoryDetailSelector,
} from 'modules/job-categories/duck';
import {
  jobTypeListByCategorySelector,
  listPopular,
  listByCategoryId,
} from 'modules/job-types/duck';
import CategoryPage from 'modules/marketing-pages/components/Category';
import { listBlogPostsByCategory } from 'modules/marketing-pages/duck';
import { getProCount, getInternalBlogPostsByCategoryId } from 'modules/marketing-pages/service';
import type { Category, InternalBlogPostItem } from 'modules/marketing-pages/marketing-page.types';
import type { RootState } from 'store/types';
import { setCachedStates } from 'store/actions';
import {
  checkAndRedirectIfNeeded,
  CATEGORY_WITH_ONLY_ONE_SUB_CATEGORY,
} from 'utils/marketing-pages';
import { isDefined } from 'utils/type-guards';
import { wrapper } from 'store/index';
import { setupPageAndLoadTranslations } from 'utils/page';
import { LOCALES } from 'next-i18next.config';
import { MarketingPageContext } from 'src/context/marketing-page';
import FeatureFlagsService from 'src/services/feature-flags';
import { getSSRCache, setSSRCache } from 'src/ssr-cache';
import { generateUrlWithoutUnwantedParameters } from 'src/utils/url';

const featureFlagsService = FeatureFlagsService.getInstance();

interface Props {
  categoryTree: Category;
  crumbs: Array<{ link: string; text: string }>;
  internalBlogPosts: Array<InternalBlogPostItem>;
}

const CategoryPageParent: NextPage<Props> = ({ categoryTree, crumbs, internalBlogPosts }) => (
  <MarketingPageContext.Provider value={{ internalBlogPosts }}>
    <CategoryPage categoryTree={categoryTree} crumbs={crumbs} />
  </MarketingPageContext.Provider>
);

type CategoryBase = {
  _id: string;
  id: string;
  title: string;
};
type Category1 = CategoryBase & {
  children?: Record<string, Category2>;
};
type Category2 = CategoryBase & {
  children?: Record<string, Category3>;
};
type Category3 = CategoryBase;

function generateCategoryTreeAndCrumbs(categories: Array<string>, tree: Category1, locale: string) {
  const prefix = locale === LOCALES[0] ? '' : `/${locale}`;
  const [, category2 = '', category3 = ''] = categories;
  const category2Tree = tree.children?.[category2];
  const category3Tree = category2Tree?.children?.[category3];

  const crumbs: Props['crumbs'] = [
    {
      link: [prefix, tree._id].join('/'),
      text: tree.title,
    },
  ];

  if (category2 && category2Tree) {
    crumbs.push({
      link: [prefix, tree._id, category2Tree._id].join('/'),
      text: category2Tree.title,
    });
  }
  if (category3 && category3Tree) {
    crumbs.push({
      link: [prefix, tree._id, category3Tree._id].join('/'),
      text: category3Tree.title,
    });
  }

  return {
    categoryTree: category3Tree ?? category2Tree ?? tree,
    crumbs,
  };
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, defaultLocale, locale, resolvedUrl, query }) => {
      const { dispatch, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      if (resolvedUrl) {
        const reductedResolvedUrl = generateUrlWithoutUnwantedParameters(resolvedUrl);
        if (reductedResolvedUrl !== resolvedUrl) {
          // The permanent redirection here is done to prevent google search console from flagging page as duplicate
          return {
            redirect: {
              destination: reductedResolvedUrl,
              permanent: true,
            },
          };
        }
      }

      const cacheKeyProps = {
        locale,
        pageName: 'category',
        query,
        url: req.url ?? '',
      };
      const cachedProps = await getSSRCache(cacheKeyProps, 'props');
      const cachedStates = await getSSRCache(cacheKeyProps, 'states');
      if (cachedProps && cachedStates) {
        dispatch(setCachedStates(cachedStates as RootState));
        await logicMiddleware.whenComplete();
        return { props: cachedProps };
      }

      const {
        category1 = CATEGORY_WITH_ONLY_ONE_SUB_CATEGORY,
        category2,
        category3,
      } = query as {
        [key: string]: string | undefined;
        category1: string;
      };

      const redirectionResult = await checkAndRedirectIfNeeded<Props>({
        categories: [category1, category2, category3].filter(isDefined),
        dispatch,
        logicMiddleware,
        store,
        url: resolvedUrl,
      });
      if (redirectionResult) {
        return redirectionResult;
      }

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'form', 'marketingPages', 'request'],
        // @ts-expect-error Extended store
        store,
      });

      const categoryId = category3 ?? category2 ?? category1;
      // TODO(micah): Remove toggle after successful release. Add exclusion for HOME if it is still excluded.
      const enabledInternalBlogPostsForC1 =
        await featureFlagsService.getEnabledInternalBlogPostsByCategoryOne();
      let internalBlogPostItems: Array<InternalBlogPostItem> = [];
      if (enabledInternalBlogPostsForC1.has(category1)) {
        internalBlogPostItems = await getInternalBlogPostsByCategoryId({
          categoryId,
        });
      } else {
        dispatch(listBlogPostsByCategory(categoryId));
      }

      dispatch(listPopular({ categoryId }));
      dispatch(getCategoryTree(category1));
      dispatch(getCategoryDetail(categoryId));
      dispatch(listByCategoryId(categoryId));

      await logicMiddleware.whenComplete();

      const verticals = jobTypeListByCategorySelector(store.getState());
      const relatedVerticals = Object.values(verticals).filter((vert) =>
        vert.categories?.includes(categoryId)
      );

      if (relatedVerticals.length === 1) {
        const [relatedVertical] = relatedVerticals;

        if (relatedVertical.categories && relatedVertical._id) {
          const { count: proCount } = await getProCount({
            jobTypeId: relatedVertical._id,
            language: currentLocale,
          }).catch(() => ({
            count: 0,
          }));

          if (proCount > 0) {
            return {
              redirect: {
                destination: `${
                  currentLocale === 'en' ? '/en' : ''
                }/${relatedVertical.categories.join('/')}/${relatedVertical._id}`,
                permanent: false,
              },
            };
          }
        }
      }

      const jobCategoryDetail = jobCategoryDetailSelector(store.getState()) as
        | Record<string, any>
        | undefined;
      const isValidCategory = Boolean(jobCategoryDetail?.id);
      if (!isValidCategory) {
        return { notFound: true };
      }

      const tree = categoryTreeSelector(store.getState()) as Category1;

      const serverSideProps = {
        ...i18nData,
        ...generateCategoryTreeAndCrumbs(
          [category1, category2, category3].filter(isDefined),
          tree,
          currentLocale
        ),
        internalBlogPosts: internalBlogPostItems,
      };

      setSSRCache(cacheKeyProps, serverSideProps);
      setSSRCache(cacheKeyProps, store.getState(), 'states');

      return { props: serverSideProps };
    }
);

export default CategoryPageParent;
